import { useEffect, useRef, useState, type ReactNode } from "react";

type StartupPhase = "enter" | "shown" | "leave" | "done";
const ENTRY_DELAY_MS = 40;
const FADE_DURATION_MS = 500;
const LOGO_HOLD_MS = 2000;
const SKIP_DURATION_MS = 180;

export function HajimiStartup({ children }: { children: ReactNode }) {
  const [phase, setPhase] = useState<StartupPhase>("enter");
  const sequenceStarted = useRef(false);
  const skipStarted = useRef(false);
  const timers = useRef<number[]>([]);
  useEffect(() => {
    return () => { for (const timer of timers.current) window.clearTimeout(timer); };
  }, []);
  useEffect(() => {
    if (phase === "done") document.documentElement.classList.remove("startup-pending");
  }, [phase]);
  if (phase === "done") return <>{children}</>;
  const schedule = (next: StartupPhase, delay: number) => timers.current.push(window.setTimeout(() => setPhase(next), delay));
  const startSequence = () => {
    if (sequenceStarted.current || skipStarted.current) return;
    sequenceStarted.current = true;
    const reducedMotion = typeof window.matchMedia === "function" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const fadeDuration = reducedMotion ? 0 : FADE_DURATION_MS;
    const leaveAt = ENTRY_DELAY_MS + fadeDuration + LOGO_HOLD_MS;
    schedule("shown", ENTRY_DELAY_MS);
    schedule("leave", leaveAt);
    schedule("done", leaveAt + fadeDuration);
  };
  const skip = () => {
    if (skipStarted.current || phase === "leave") return;
    skipStarted.current = true;
    for (const timer of timers.current) window.clearTimeout(timer);
    timers.current = [];
    setPhase("leave");
    schedule("done", SKIP_DURATION_MS);
  };
  return <button className={`hajimi-startup ${phase}`} type="button" aria-label="跳过 Hajimi Productions 启动画面" onClick={skip}>
    <span className="hajimi-brand" data-testid="startup-branding">
      <img src="/art/branding/hajimi-productions.jpg" alt="Hajimi Productions" onLoad={startSequence} onError={startSequence} />
      <span className="hajimi-motto" aria-label="始于一念，成于众志">
        <i aria-hidden="true" />
        <span>始于一念</span>
        <b aria-hidden="true" />
        <span>成于众志</span>
        <i aria-hidden="true" />
      </span>
    </span>
  </button>;
}
